import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { cn } from "@/lib/utils";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { resolveStatus } from "@/domains/pm/statuses/statusCatalogue";
import type { PMIssue } from "@/domains/pm/types";
import { isIssueOverdue } from "@/domains/pm/utils/dueDate";
import { isDateRangeInverted } from "@/domains/pm/utils/issueDates";
import { barRect, type GanttScale } from "@/domains/pm/utils/ganttScale";
import {
  DRAG_ACTIVATION_PX,
  previewDates,
  type GanttDates,
  type GanttDragMode,
} from "@/domains/pm/utils/ganttDrag";
import {
  GANTT_BAR_HEIGHT,
  GANTT_CONNECTOR_OFFSET,
  GANTT_CONNECTOR_R,
  GANTT_HANDLE_PX,
} from "./ganttConstants";
import { useScheduleDragPreviewStore } from "../schedule/scheduleDragPreview";
import { isDraftIssue } from "@/domains/pm/issueDraft";
import { useLanguage } from "@/lib/i18n/LanguageContext";

interface DragState {
  mode: GanttDragMode;
  dx: number;
  /**
   * Which way the gesture went. Decided ONCE, at activation, from whichever
   * axis had travelled further — a drag either changes the dates or changes
   * where the bar sits, never both, and re-deciding mid-gesture would make a
   * wobbling hand flip between the two.
   */
  axis: "x" | "y";
  /** Travelled past the activation threshold — this is a drag, not a click. */
  live: boolean;
}

interface GanttBarProps {
  issue: PMIssue;
  scale: GanttScale;
  selected: boolean;
  /** Committed on pointer-up, with the total travel in px. */
  /** Omitted in snapshot mode, which makes the bar read-only. */
  onCommit?: (issue: PMIssue, mode: GanttDragMode, dxPx: number) => void;
  /** A press that never became a drag is a click. */
  onSelect: (issue: PMIssue) => void;
  /** Held while any bar is being dragged, so the page can suppress the
   *  realtime reload that would otherwise re-materialise the list mid-drag. */
  /** Omitted by a read-only pane, which has no drag to report. */
  onDragActiveChange?: (active: boolean) => void;
  label: (issue: PMIssue) => string;
  /** Start dragging a dependency from one end of this bar. Omitted where
   *  linking is not offered (no provider, or a read-only surface). */
  onLinkStart?: (issue: PMIssue, side: "start" | "end", e: React.PointerEvent) => void;
  /** True while ANY link is being dragged: every bar shows its connectors so
   *  the drop targets are visible, not just the one under the cursor. */
  linking?: boolean;
  /** Drawn provisionally: moved, not yet confirmed. */
  staged?: boolean;
  /** The gesture went vertical: report where it is so the view can show a drop
   *  indicator. `null` means the gesture was abandoned. */
  onVerticalMove?: (issue: PMIssue, clientY: number | null) => void;
  /** Released after a vertical drag — reorder, or change the band's attribute. */
  onVerticalDrop?: (issue: PMIssue, clientY: number) => void;
}

/**
 * One issue's bar.
 *
 * Owns pointer capture and the live preview; the maths is in ganttDrag. The
 * bar is a real <button> so it is reachable and announces its span — there is
 * no keyboard drag, and that is deliberate rather than an oversight: the
 * detail panel's two date pickers are the accessible way to reschedule, and a
 * keyboard drag would need its own selection model to be worth having.
 *
 * The write happens ONCE, on pointer-up. Writing per frame would mean a PATCH,
 * an activity row and a realtime echo for every pixel of a drag.
 */
export const GanttBar = observer(function GanttBar({
  issue,
  scale,
  selected,
  onCommit,
  onSelect,
  onDragActiveChange,
  label,
  onLinkStart,
  linking = false,
  staged = false,
  onVerticalMove,
  onVerticalDrop,
}: GanttBarProps) {
  // The drag lives in a REF, with a state mirror purely so the bar re-renders
  // as it moves. The ref is what every handler reads.
  //
  // Reading it from render state instead drops whole gestures: the handlers
  // close over the state from their last render, so any pointerup that lands
  // in the same frame as the move that promoted the drag still sees
  // `live: false` and takes the click path. Real pointer events usually
  // arrive in separate tasks, which hides this — until a fast flick, a
  // coalesced event or a busy frame does not.
  const dragRef = useRef<DragState | null>(null);
  // Where this bar is being dragged to, for the open issue's panel. Written
  // only for a date drag, and cleared however the gesture ends.
  const dragPreview = useScheduleDragPreviewStore();
  const clearPreview = () => {
    if (dragPreview?.get()?.issueId === issue.id) dragPreview.set(null);
  };
  const { catalogue } = useStatusCatalogue();
  const { t } = useLanguage();
  // A new issue still being written: no number to show, and never a link
  // target — `useLinkGesture` finds targets by `data-gantt-bar-issue`.
  const draft = isDraftIssue(issue);
  const [, forceRender] = useState(0);
  const drag = dragRef.current;
  const setDrag = (next: DragState | null) => {
    dragRef.current = next;
    forceRender((n) => n + 1);
  };
  const startX = useRef(0);
  const startY = useRef(0);

  // The active flag must not outlive the bar. An unmount mid-drag (a filter
  // change, a realtime removal) releases pointer capture without firing
  // pointerup or pointercancel, so endDrag never runs — and the page would
  // suppress realtime reloads for the rest of its life. The callback is read
  // from a ref so the cleanup can run on unmount only, not on every re-render
  // that hands us a new function identity.
  const dragActiveChange = useRef(onDragActiveChange);
  dragActiveChange.current = onDragActiveChange;
  // The same goes for the preview: a bar unmounted mid-drag would otherwise
  // leave the panel showing dates nobody is dragging.
  const unmountPreview = useRef({ store: dragPreview, issueId: issue.id });
  unmountPreview.current = { store: dragPreview, issueId: issue.id };
  useEffect(
    () => () => {
      if (dragRef.current?.live) dragActiveChange.current?.(false);
      const { store, issueId } = unmountPreview.current;
      if (store?.get()?.issueId === issueId) store.set(null);
    },
    []
  );

  const committed: GanttDates = { start: issue.start_date, due: issue.due_date };
  // Mid-drag the bar is drawn from the preview, so what you see is what a
  // release will write.
  const shown =
    drag?.live && drag.axis === "x" && drag.dx !== 0
      ? previewDates(scale, committed, drag.mode, drag.dx)
      : committed;

  // A one-sided issue occupies its single known day; issueDateRange orders the
  // ends so an inverted pair still draws left-to-right.
  const from = shown.start ?? shown.due;
  const to = shown.due ?? shown.start;
  if (!from || !to) return null;
  const [lo, hi] = from <= to ? [from, to] : [to, from];
  const rect = barRect(scale, lo, hi);

  const oneSided = !shown.start || !shown.due;
  const inverted = isDateRangeInverted({ start_date: shown.start, due_date: shown.due });
  const overdue = isIssueOverdue({ status: issue.status, due_date: shown.due });
  // Through the catalogue, never the built-in map: a project's own status is
  // not in there, and indexing it returned undefined -- which took the whole
  // Gantt down with "cannot read properties of undefined (reading 'bgColor')".
  const config = resolveStatus(catalogue, issue.building_project_id, issue.status);

  const beginDrag = (mode: GanttDragMode) => (e: React.PointerEvent) => {
    // Left button only. Without this a right-press sets drag state and the
    // pointerup takes the click path, so opening the context menu also
    // selected the issue.
    if (e.button !== 0) return;
    // Snapshot mode has no commit, so there is nothing a drag could do.
    if (!onCommit) return;
    e.stopPropagation();
    // Best-effort: capture keeps the drag alive when the cursor leaves the
    // bar, but a failure to capture must not stop the drag starting.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // no capture — the gesture still works while the pointer stays inside
    }
    startX.current = e.clientX;
    startY.current = e.clientY;
    setDrag({ mode, dx: 0, axis: "x", live: false });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const current = dragRef.current;
    if (!current) return;
    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;
    if (!current.live) {
      // Promote to a real drag only past the threshold, so a plain click still
      // reaches onSelect (the Kanban's PointerSensor uses the same distance).
      // Measured across both axes now, or a purely vertical drag would never
      // activate at all.
      if (Math.hypot(dx, dy) < DRAG_ACTIVATION_PX) return;
      // Re-anchor on activation: travel is measured from HERE, not from
      // pointer-down. At month granularity a day is 4px and the threshold is
      // 5, so keeping the pointer-down anchor would make the smallest drag
      // the UI recognises commit a one-day shift the moment it activates.
      startX.current = e.clientX;
      startY.current = e.clientY;
      // Only the body can be dragged vertically: the edge grips mean "change
      // this date", and a resize that jumped rows would be nonsense. Nor can a
      // bar whose row the user may not rearrange — that drag stays on dates.
      const axis: "x" | "y" =
        current.mode === "move" && onVerticalDrop && Math.abs(dy) > Math.abs(dx) ? "y" : "x";
      onDragActiveChange?.(true);
      setDrag({ mode: current.mode, dx: 0, axis, live: true });
      return;
    }
    setDrag({ mode: current.mode, dx, axis: current.axis, live: true });
    if (current.axis === "x") {
      const dates = previewDates(
        scale,
        { start: issue.start_date, due: issue.due_date },
        current.mode,
        dx
      );
      dragPreview?.set({ issueId: issue.id, start: dates.start, due: dates.due });
    }
    if (current.axis === "y") onVerticalMove?.(issue, e.clientY);
  };

  // Gesture lost rather than finished: pointer capture taken away, or the
  // browser cancelling the pointer. No pointerup follows, and the travel we
  // last saw is not what the user meant to commit — so drop the drag, write
  // nothing, and clear the flag. Fires after a normal pointerup too, where
  // endDrag has already cleared dragRef and this is a no-op.
  const abortDrag = () => {
    const current = dragRef.current;
    if (!current) return;
    setDrag(null);
    if (current.live) {
      onDragActiveChange?.(false);
      if (current.axis === "y") onVerticalMove?.(issue, null);
    }
    clearPreview();
  };

  const endDrag = (e: React.PointerEvent) => {
    const current = dragRef.current;
    if (!current) return;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setDrag(null);
    if (current.live) {
      onDragActiveChange?.(false);
      if (current.axis === "y") onVerticalDrop?.(issue, e.clientY);
      else onCommit?.(issue, current.mode, current.dx);
    } else {
      onSelect(issue);
    }
    // After the commit, which has already put the new dates in the store or the
    // staging buffer — clearing first would show the old ones for a frame.
    clearPreview();
  };

  return (
    <button
      type="button"
      aria-label={label(issue)}
      // A read-only chart has no drag, so `beginDrag` bails and the pointer-up
      // path never reaches `onSelect` — the bar simply did nothing when
      // clicked. Selection is the one thing a snapshot's bars SHOULD still do.
      onClick={onCommit ? undefined : () => onSelect(issue)}
      onPointerDown={beginDrag("move")}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={abortDrag}
      onLostPointerCapture={abortDrag}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(issue);
        }
      }}
      className={cn(
        "group/bar absolute top-1/2 -translate-y-1/2 rounded-[3px] border text-left transition-shadow",
        // Provisional: the cascade has not run, so this is the only signal
        // that the drag registered and that more will move on Confirm.
        staged && "outline outline-1 outline-offset-1 outline-[#d97706]",
        // Darker for the whole gesture, either axis — the bar under the
        // cursor should read as picked up rather than merely hovered.
        drag?.live && "shadow-md brightness-[0.92]",
        // touch-action must be none or the browser scrolls instead of dragging.
        "touch-none select-none",
        draft ? "cursor-pointer" : drag?.live ? "cursor-grabbing shadow-md" : "cursor-grab",
        selected && "ring-2 ring-primary ring-offset-1",
        oneSided && "border-dashed",
        // The bar a link being drawn would land on, marked by the gesture
        // itself (useLinkGesture) so the pointer loop never re-renders a row.
        "data-[link-target=ok]:ring-2 data-[link-target=ok]:ring-[#ef4129] data-[link-target=ok]:ring-offset-1",
        "data-[link-target=refused]:ring-2 data-[link-target=refused]:ring-[#9ca3af] data-[link-target=refused]:ring-offset-1"
      )}
      style={{
        // A vertical drag moves the whole ROW (GanttView lifts it), so the bar
        // itself stays put inside it.
        left: rect.left,
        width: rect.width,
        height: GANTT_BAR_HEIGHT,
        backgroundColor: inverted ? "#fcebeb" : config.bgColor,
        borderColor: inverted ? "#ef4129" : overdue ? "#ef4129" : config.dotColor,
      }}
      data-gantt-bar-issue={draft ? undefined : issue.id}
      data-draft-issue={draft ? issue.id : undefined}
    >
      <span
        className="pointer-events-none block truncate px-1.5 text-[10px] font-medium leading-[18px]"
        style={{ color: inverted ? "#ef4129" : config.textColor }}
      >
        {draft ? t.pm.issues.draft.marker : `#${issue.issue_number}`}
      </span>

      {/* Edge handles. On a one-sided bar the FREE edge is how the missing
          date gets created, which is why both are always present — except on
          a draft, whose dates are set in the draft panel. */}
      {!draft && (
        <>
          <span
            role="presentation"
            onPointerDown={beginDrag("resize-start")}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={abortDrag}
            onLostPointerCapture={abortDrag}
            className="absolute inset-y-0 left-0 cursor-col-resize touch-none"
            style={{ width: GANTT_HANDLE_PX }}
          />
          <span
            role="presentation"
            onPointerDown={beginDrag("resize-end")}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={abortDrag}
            onLostPointerCapture={abortDrag}
            className="absolute inset-y-0 right-0 cursor-col-resize touch-none"
            style={{ width: GANTT_HANDLE_PX }}
          />
        </>
      )}

      {/* Connector dots. They sit OUTSIDE the bar, beyond the resize grips, so
          the two gestures never compete for the same pixels. Which pair of
          ends a link joins is what names the relationship — right-to-left is
          finish-to-start, left-to-left is start-to-start, and so on. */}
      {onLinkStart &&
        (["start", "end"] as const).map((side) => (
          <span
            key={side}
            role="presentation"
            data-gantt-bar-issue={issue.id}
            data-connector-side={side}
            onPointerDown={(e) => {
              if (e.button !== 0) return;
              e.stopPropagation();
              onLinkStart(issue, side, e);
            }}
            className={cn(
              "absolute top-1/2 -translate-y-1/2 rounded-full border border-background bg-foreground/70 transition-[opacity,transform]",
              // The dot stays small, but its target is not: an 8px circle
              // outside the bar is hard to hit, and missing it starts a bar
              // drag instead. The pseudo-element hit-tests as the dot.
              "before:absolute before:-inset-1 before:rounded-full before:content-['']",
              "data-[link-target]:scale-150 data-[link-target=ok]:bg-[#ef4129] data-[link-target=refused]:bg-[#9ca3af]",
              linking ? "opacity-100" : "opacity-0 group-hover/bar:opacity-100"
            )}
            style={{
              [side === "start" ? "left" : "right"]: -GANTT_CONNECTOR_OFFSET,
              width: GANTT_CONNECTOR_R * 2,
              height: GANTT_CONNECTOR_R * 2,
              cursor: "crosshair",
            }}
          />
        ))}
    </button>
  );
});
